/**
 * CopyDeter — image screenshot/copy deterrence library.
 *
 * Originally sourced from ShootProof/copydeter@v1.0.2
 * (commit 722675be74b1df15e88cbdc05116c39bf77b40d8). Previously consumed as an
 * npm dependency; moved in-repo so it is maintained alongside its only consumers
 * (client gallery + Studio gallery preview). Edit here — there is no external package.
 */
import { normalizeConfig, validateConfig } from './core/config';
import { isMobileDevice } from './core/detector';
import { ElementObserver } from './core/observer';
import { createBlurOverlay, removeBlurOverlay } from './mobile/blur-overlay';
import {
    attachTouchHandlers,
    detachTouchHandlers,
    setTouchHandlerDebug
} from './mobile/touch-handler';
import { ScreenshotListener } from './screenshot/listener';
import type { CopyDeterConfig, ProtectionState } from './types';

// Re-export types for consumers
export type { CopyDeterConfig, ProtectionState };

/**
 * Internal state
 */
let config: Required<CopyDeterConfig> | null = null;
let screenshotListener: ScreenshotListener | null = null;
let elementObserver: ElementObserver | null = null;
let _isInitialized = false;
let isMobile = false;

/**
 * Debug logger - only logs when debug mode is enabled
 */
function log(...args: unknown[]): void {
    if (config?.debug) {
        console.log('[CopyDeter]', ...args);
    }
}

/**
 * Initializes CopyDeter with the provided configuration.
 * Starts protecting elements matching targetSelectors.
 *
 * @throws {Error} If configuration is invalid or already initialized
 */
export function init(userConfig: CopyDeterConfig): void {
    if (_isInitialized) {
        throw new Error('CopyDeter is already initialized. Call destroy() first to reinitialize.');
    }

    // Validate and normalize config
    validateConfig(userConfig);
    config = normalizeConfig(userConfig);

    // Set debug mode for touch handlers
    setTouchHandlerDebug(config.debug);

    // Detect mobile (or force it for testing)
    isMobile = config.forceMobile || isMobileDevice();

    log('Initializing', {
        isMobile,
        forceMobile: config.forceMobile,
        isMobileDevice: isMobileDevice(),
        screenshotProtectionEnabled: config.screenshotProtectionEnabled,
        mobileProtectionEnabled: config.mobileProtectionEnabled,
        targetSelectors: config.targetSelectors
    });

    // Set up screenshot / window-focus protection if either is enabled
    if (config.screenshotProtectionEnabled || config.windowFocusProtectionLevel !== 'off') {
        screenshotListener = new ScreenshotListener(
            config.overlayHtml,
            config.screenshotProtectionEnabled,
            config.windowFocusProtectionLevel,
            config.windowFocusOverlayHtml
        );
        screenshotListener.start();
        log('Screenshot listener started', {
            keyboard: config.screenshotProtectionEnabled,
            windowFocus: config.windowFocusProtectionLevel
        });
    }

    // Set up element observer for mobile protection
    if (config.mobileProtectionEnabled && config.targetSelectors.length > 0) {
        elementObserver = new ElementObserver();
        elementObserver.start(config.targetSelectors, handleElementAdded, handleElementRemoved);
        log('Element observer started');
    }

    _isInitialized = true;
    log('Initialization complete');
}

/**
 * Destroys CopyDeter, removing all listeners, observers, and protections.
 * Can be called safely even if not initialized.
 */
export function destroy(): void {
    // Stop screenshot listener
    if (screenshotListener) {
        screenshotListener.stop();
        screenshotListener = null;
    }

    // Stop observer and clean up protected elements
    if (elementObserver) {
        elementObserver.stop();
        elementObserver = null;
    }

    // Reset state
    config = null;
    _isInitialized = false;
    isMobile = false;
}

/**
 * Manually applies protection to a specific element.
 * Useful when you need explicit control over protection timing.
 *
 * @throws {Error} If CopyDeter is not initialized
 */
export function protect(element: Element): void {
    if (!_isInitialized || !config) {
        throw new Error('CopyDeter must be initialized before calling protect()');
    }

    if (!elementObserver) {
        throw new Error('Mobile protection is not enabled');
    }

    if (elementObserver.isProtected(element)) {
        return; // Already protected
    }

    applyProtection(element);
}

/**
 * Manually removes protection from a specific element.
 *
 * @throws {Error} If CopyDeter is not initialized
 */
export function unprotect(element: Element): void {
    if (!_isInitialized || !elementObserver) {
        return; // Nothing to do
    }

    const state = elementObserver.getProtection(element);

    if (!state) {
        return; // Not protected
    }

    removeProtection(element, state);
}

/**
 * Returns whether CopyDeter is currently initialized
 */
export function isInitialized(): boolean {
    return _isInitialized;
}

/**
 * Returns debug info about current state
 */
export function getDebugInfo(): object {
    const protectedCount = elementObserver ? elementObserver.getProtectedCount() : 0;
    return {
        isInitialized: _isInitialized,
        isMobile,
        forceMobile: config?.forceMobile,
        isMobileDevice: isMobileDevice(),
        protectedElementCount: protectedCount,
        config: config
            ? {
                  screenshotProtectionEnabled: config.screenshotProtectionEnabled,
                  mobileProtectionEnabled: config.mobileProtectionEnabled,
                  targetSelectors: config.targetSelectors,
                  debug: config.debug
              }
            : null
    };
}

/**
 * Handles a new element being added to the DOM that matches targetSelectors
 */
function handleElementAdded(element: Element): void {
    applyProtection(element);
}

/**
 * Handles an element being removed from the DOM
 */
function handleElementRemoved(element: Element, state: ProtectionState): void {
    removeProtection(element, state);
}

/**
 * Applies protection to an element
 */
function applyProtection(element: Element): void {
    if (!elementObserver) {
        return;
    }

    log('Protecting element', element);

    const htmlElement = element as HTMLElement;

    // Create initial state
    const state: ProtectionState = {
        element,
        blurSwapped: false
    };

    // Prevent drag-to-save
    htmlElement.draggable = false;

    const dragStartHandler = (e: Event): void => {
        e.preventDefault();
        log('Drag prevented');
    };

    htmlElement.addEventListener('dragstart', dragStartHandler);
    state.dragStartHandler = dragStartHandler;

    // Prevent right-click save
    const contextMenuHandler = (e: Event): void => {
        e.preventDefault();
        log('Context menu prevented');
    };

    htmlElement.addEventListener('contextmenu', contextMenuHandler);
    state.contextMenuHandler = contextMenuHandler;

    // Only apply mobile blur protection on actual mobile devices
    if (isMobile) {
        log('Applying mobile blur (isMobile=true)');
        const blurState = createBlurOverlay(element, config?.mobileBlurType);
        Object.assign(state, blurState);

        log('Blur overlay created', {
            container: state.container,
            blurOverlayTL: state.blurOverlayTL,
            blurOverlayBR: state.blurOverlayBR,
            containerStyles: state.container?.style.cssText,
            tlStyles: state.blurOverlayTL?.style.cssText,
            brStyles: state.blurOverlayBR?.style.cssText
        });

        // Attach touch handlers
        attachTouchHandlers(state);
        log('Touch handlers attached');
    } else {
        log('Skipping mobile blur (isMobile=false)');
    }

    // Register with observer
    elementObserver.registerProtection(element, state);
}

/**
 * Removes protection from an element
 */
function removeProtection(element: Element, state: ProtectionState): void {
    const htmlElement = element as HTMLElement;

    // Remove drag prevention
    if (state.dragStartHandler) {
        htmlElement.removeEventListener('dragstart', state.dragStartHandler);
    }

    htmlElement.draggable = true;

    // Remove context menu prevention
    if (state.contextMenuHandler) {
        htmlElement.removeEventListener('contextmenu', state.contextMenuHandler);
    }

    // Detach touch handlers
    detachTouchHandlers(state);

    // Remove blur overlays and unwrap
    removeBlurOverlay(state);

    // Unregister from observer
    elementObserver?.unregisterProtection(element);
}
